import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Calendar, Clock, Tag, ArrowLeft, ChevronLeft, ChevronRight, ArrowRight, Share2, Linkedin, Facebook, Mail } from 'lucide-react';
import { PublicHeader } from '../PublicHeader';
import { Footer } from '../Footer';
import { supabase } from '../../lib/supabase';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { PreviewComponents } from '../shared/MarkdownComponents';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  category: string;
  featured: boolean;
  read_time: string;
  content: string;
  keywords: string[];
  published: boolean;
}

interface RelatedPost {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  date: string;
}

export function BlogPost() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [prevPost, setPrevPost] = useState<RelatedPost | null>(null);
  const [nextPost, setNextPost] = useState<RelatedPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<RelatedPost[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadPost();
  }, [postId]); // Add postId as dependency to reload when it changes

  const loadPost = async () => {
    try {
      if (!postId) {
        setError('No post ID provided');
        return;
      }

      // Load current post
      const { data: currentPost, error: fetchError } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('id', postId)
        .eq('published', true)
        .single();

      if (fetchError) throw fetchError;
      if (!currentPost) throw new Error('Post not found');

      setPost(currentPost);

      // Load adjacent posts
      const { data: adjacentPosts } = await supabase
        .from('blog_posts')
        .select('id, title, excerpt, category, date')
        .eq('published', true)
        .or(`date.gt.${currentPost.date},date.lt.${currentPost.date}`)
        .order('date', { ascending: true })
        .limit(2);

      if (adjacentPosts) {
        const prevPost = adjacentPosts.find(p => new Date(p.date) < new Date(currentPost.date));
        const nextPost = adjacentPosts.find(p => new Date(p.date) > new Date(currentPost.date));
        setPrevPost(prevPost || null);
        setNextPost(nextPost || null);
      }

      // Load related posts
      const { data: relatedPosts } = await supabase
        .from('blog_posts')
        .select('id, title, excerpt, category, date')
        .eq('published', true)
        .eq('category', currentPost.category)
        .neq('id', currentPost.id)
        .limit(3);

      if (relatedPosts) {
        setRelatedPosts(relatedPosts);
      }

      setError(null);
    } catch (err) {
      console.error('Error loading blog post:', err);
      setError('Failed to load blog post');
    } finally {
      setLoading(false);
    }
  };

  const getShareUrls = () => {
    const currentUrl = window.location.href;
    const title = post?.title || '';
    const text = post?.excerpt || '';

    return {
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(title)}`,
      email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(text)}\n\n${encodeURIComponent(currentUrl)}`
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bw-surface-subtle">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-servet-blue"></div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-bw-surface-subtle">
        <PublicHeader />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-bw-surface rounded-lg shadow-lg p-8 text-center">
            <h1 className="text-2xl font-bold text-bw-ink mb-4">
              {error || 'Post not found'}
            </h1>
            <button
              onClick={() => navigate('/blog')}
              className="inline-flex items-center px-4 py-2 bg-bw-primary text-white rounded-md hover:bg-bw-primary/90"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Blog
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-bw-app">
      <PublicHeader />
      
      <div className="flex-grow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="relative bg-bw-sidebar rounded-lg shadow-lg p-12 mb-12 overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
            </div>
            
            <div className="relative text-center text-white">
              <h1 className="text-4xl font-bold mb-4">{post.title}</h1>
              <div className="flex items-center justify-center space-x-4 text-white/80">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  {post.date}
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  {post.read_time}
                </div>
                <div className="flex items-center">
                  <Tag className="h-4 w-4 mr-1" />
                  {post.category}
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-bw-surface rounded-lg shadow-lg p-8">
              <div className="bg-bw-surface-subtle border border-bw-border rounded-lg p-4 mb-8">
                <p className="text-bw-ink italic">{post.excerpt}</p>
              </div>
              
              <div className="prose prose-lg max-w-none">
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={PreviewComponents}
                >
                  {post.content}
                </ReactMarkdown>
              </div>

              <div className="mt-8 pt-4 border-t">
                <h4 className="text-sm font-medium text-bw-ink">Keywords</h4>
                <div className="flex flex-wrap gap-2 mt-2">
                  {post.keywords?.map((keyword, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-bw-surface-subtle text-bw-ink rounded-full text-sm"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>

              {/* Social Sharing Section */}
              <div className="mt-8 pt-6 border-t">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-bw-ink flex items-center">
                    <Share2 className="h-4 w-4 mr-2 text-bw-accent" />
                    Share this article
                  </h4>
                  <div className="flex items-center space-x-4">
                    <a
                      href={getShareUrls().linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-full bg-bw-surface text-bw-accent hover:bg-bw-primary hover:text-white transition-colors border border-servet-red hover:border-servet-blue"
                      title="Share on LinkedIn"
                    >
                      <Linkedin className="h-5 w-5" />
                    </a>
                    <a
                      href={getShareUrls().facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-full bg-bw-surface text-bw-accent hover:bg-bw-primary hover:text-white transition-colors border border-servet-red hover:border-servet-blue"
                      title="Share on Facebook"
                    >
                      <Facebook className="h-5 w-5" />
                    </a>
                    <a
                      href={getShareUrls().twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-full bg-bw-surface text-bw-accent hover:bg-bw-primary hover:text-white transition-colors border border-servet-red hover:border-servet-blue"
                      title="Share on X (Twitter)"
                    >
                      <svg 
                        className="h-5 w-5" 
                        fill="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                    </a>
                    <a
                      href={getShareUrls().email}
                      className="p-2 rounded-full bg-bw-surface text-bw-accent hover:bg-bw-primary hover:text-white transition-colors border border-servet-red hover:border-servet-blue"
                      title="Share via Email"
                    >
                      <Mail className="h-5 w-5" />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Post Navigation */}
            <nav className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
              {prevPost && (
                <Link 
                  to={`/blog/${prevPost.id}`}
                  className="group relative bg-bw-surface rounded-lg shadow-card p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="absolute top-1/2 -translate-y-1/2 left-4">
                    <ChevronLeft className="h-6 w-6 text-bw-primary group-hover:-translate-x-1 transition-transform" />
                  </div>
                  <div className="pl-8">
                    <span className="text-sm text-bw-muted">Previous Article</span>
                    <h4 className="text-lg font-medium text-bw-ink mt-1 line-clamp-2">
                      {prevPost.title}
                    </h4>
                  </div>
                </Link>
              )}

              <div className="text-center">
                <span className="text-sm text-bw-muted">You're reading</span>
                <h4 className="text-lg font-medium text-bw-ink mt-1 line-clamp-2">
                  {post.title}
                </h4>
              </div>

              {nextPost && (
                <Link 
                  to={`/blog/${nextPost.id}`}
                  className="group relative bg-bw-surface rounded-lg shadow-card p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="absolute top-1/2 -translate-y-1/2 right-4">
                    <ChevronRight className="h-6 w-6 text-bw-primary group-hover:translate-x-1 transition-transform" />
                  </div>
                  <div className="pr-8">
                    <span className="text-sm text-bw-muted">Next Article</span>
                    <h4 className="text-lg font-medium text-bw-ink mt-1 line-clamp-2">
                      {nextPost.title}
                    </h4>
                  </div>
                </Link>
              )}
            </nav>

            {/* Related Posts */}
            {relatedPosts.length > 0 && (
              <section className="mt-16">
                <h3 className="text-2xl font-bold text-bw-ink mb-8">Related Articles</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {relatedPosts.map((relatedPost) => (
                    <Link
                      key={relatedPost.id}
                      to={`/blog/${relatedPost.id}`}
                      className="group bg-bw-surface rounded-lg shadow-card overflow-hidden hover:shadow-lg transition-shadow"
                    >
                      <div className="relative bg-bw-sidebar p-6">
                        <div className="absolute inset-0 opacity-10">
                          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
                        </div>
                        <div className="relative">
                          <h4 className="text-lg font-medium text-white mb-2 line-clamp-2">
                            {relatedPost.title}
                          </h4>
                          <div className="flex items-center text-sm text-white/80">
                            <Calendar className="h-4 w-4 mr-1" />
                            {relatedPost.date}
                          </div>
                        </div>
                      </div>
                      <div className="p-6">
                        <p className="text-bw-muted text-sm line-clamp-3">
                          {relatedPost.excerpt}
                        </p>
                        <div className="mt-4 flex items-center justify-between">
                          <span className="text-sm font-medium text-bw-primary">
                            {relatedPost.category}
                          </span>
                          <span className="text-sm text-bw-primary group-hover:text-bw-accent flex items-center">
                            Read More
                            <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            <div className="mt-8">
              <Link
                to="/blog"
                className="inline-flex items-center px-4 py-2 bg-bw-primary text-white rounded-md hover:bg-bw-primary/90"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back to Blog
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}